import React, { useState, useEffect } from "react";
import {
  Table, TableHead, TableRow, TableCell, TableContainer, Paper,
  Button, TextField, IconButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import axios from "axios";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { resources } from "../Resourses/Resources";
import { getUserInfo } from "../Authservice";
import { TableBody } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

// Styled table cells
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#2C268A",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  cursor: "grab",
}));

// Sortable Row Component
const SortableRow = ({ 
  member, 
  index,
  onEdit, 
  onDelete, 
  isAdmin,
  isEditing,
  editedMember,
  handleEditChange,
  handleSave,
  handleCancel
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: member.hierarchyOrder.toString(),
    disabled: !isAdmin
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? "#f0f0f0" : undefined,
    cursor: isAdmin ? "grab" : "default",
  };

  return (
    <StyledTableRow 
      ref={setNodeRef} 
      style={style} 
      {...attributes} 
      {...listeners}
    >
      <StyledTableCell>{index + 1}</StyledTableCell>
      <StyledTableCell>
        {isEditing ? (
          <TextField
            size="small"
            value={editedMember.memberName || ""}
            onChange={(e) => handleEditChange("memberName", e.target.value)}
          />
        ) : (
          member.memberName
        )}
      </StyledTableCell>
      <StyledTableCell>
        {isEditing ? (
          <TextField
            size="small"
            value={editedMember.position || ""}
            onChange={(e) => handleEditChange("position", e.target.value)}
          />
        ) : (
          member.position
        )}
      </StyledTableCell>
      {isAdmin && (
        <StyledTableCell onClick={(e) => e.stopPropagation()}>
          {isEditing ? (
            <>
              <IconButton onClick={() => handleSave(member.hierarchyOrder)}>
                <SaveIcon style={{color:"#1976d2"}}/>
              </IconButton>
              <IconButton onClick={handleCancel}>
                <CancelIcon style={{color:"red"}}/>
              </IconButton>
            </>
          ) : (
            <>
              <IconButton onClick={() => onEdit(member)}>
                <EditIcon style={{color:"#1976d2"}} />
              </IconButton>
              <IconButton onClick={() => onDelete(member.hierarchyOrder)}>
                <DeleteIcon style={{color:"red"}}/>
              </IconButton>
            </>
          )}
        </StyledTableCell>
      )}
    </StyledTableRow>
  );
};

const AdministrationMembers = () => {
  const userRole = getUserInfo()?.roles[0];
  const isAdmin = userRole === "ROLE_MANAGEMENT";
  const [members, setMembers] = useState([]);
  const [displayOrder, setDisplayOrder] = useState([]);
  const [newMember, setNewMember] = useState({ memberName: "", position: "" });
  const [editId, setEditId] = useState(null);
  const [editedMember, setEditedMember] = useState({});
  const [orderChanged, setOrderChanged] = useState(false);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(resources.APPLICATION_URL + "getAllGoverningMembers");
      if (response.data.length > 0) {
        setMembers(response.data);
        setDisplayOrder([...response.data]);
        setOrderChanged(false);
      }
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  const handleDragEnd = (event) => {
    if (!isAdmin) return;
    
    const { active, over } = event;
    
    if (!active || !over || active.id === over.id) return;
    
    const oldIndex = displayOrder.findIndex(item => item.hierarchyOrder.toString() === active.id);
    const newIndex = displayOrder.findIndex(item => item.hierarchyOrder.toString() === over.id);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      // Just reorder the display array without changing hierarchyOrder values
      const newDisplayOrder = arrayMove([...displayOrder], oldIndex, newIndex);
      setDisplayOrder(newDisplayOrder);
      setOrderChanged(true);
    }
  };

  // Function to save the current display order to the backend
  const saveOrder = async () => {
    try {
      await axios.post(resources.APPLICATION_URL + "saveGoverningMembers", displayOrder, {
        headers: { "Content-Type": "application/json" },
      });
      alert('Order saved successfully');
      fetchData();
    } catch (e) {
      console.error("Error saving order:", e);
      alert('Failed to save order');
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!newMember.memberName || !newMember.position) return;
    
    const newRow = {
      memberName: newMember.memberName,
      position: newMember.position,
    };
    
    const updatedMembers = [...members, newRow];
    
    try {
      const res = await axios.post(resources.APPLICATION_URL + "saveGoverningMembers", 
        updatedMembers, 
        { headers: { "Content-Type": "application/json" } }
      );
      
      if (res.status === 200) {
        fetchData();
        setNewMember({ memberName: "", position: "" });
        alert("Member added successfully!");
      }
    } catch (error) {
      console.error("Error saving data", error);
    }
  };

  const handleDelete = async (hierarchyOrder) => {
    if (!window.confirm("Are you sure you want to delete this member?")) {
      return;
    }
    
    try {
      const res = await axios.delete(
        resources.APPLICATION_URL + "deleteGoverningBodyByPosition?orderId=" + hierarchyOrder
      );
      if (res.status === 200) {
        fetchData();
        alert("Member deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting member", error);
    }
  };

  const handleEdit = (member) => {
    setEditId(member.hierarchyOrder);
    setEditedMember({ 
      memberName: member.memberName, 
      position: member.position 
    });
  };

  const handleEditChange = (field, value) => {
    setEditedMember(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (hierarchyOrder) => {
    const updatedMembers = members.map(member => 
      member.hierarchyOrder === hierarchyOrder 
        ? { ...member, memberName: editedMember.memberName, position: editedMember.position } 
        : member
    );
    
    try {
      await axios.post(resources.APPLICATION_URL + "saveGoverningMembers", updatedMembers, {
        headers: { "Content-Type": "application/json" },
      });
      fetchData();
      setEditId(null);
      setEditedMember({});
      alert("Member updated successfully!");
    } catch (e) {
      console.error("Error updating member:", e);
    }
  };

  const handleCancel = () => {
    setEditId(null);
    setEditedMember({});
  };

  return (
    <div style={{ padding: "2rem" }}>
      {isAdmin && (
        <div style={{ padding: 16, display: "flex", gap: 8, flexWrap: "wrap", marginBottom: "1rem" }}>
          <TextField
            label="Member Name"
            value={newMember.memberName}
            onChange={e => setNewMember({ ...newMember, memberName: e.target.value })}
          />
          <TextField
            label="Position"
            value={newMember.position}
            onChange={e => setNewMember({ ...newMember, position: e.target.value })}
          />
          <Button variant="contained" onClick={handleSubmit}>Add</Button>
          
          {orderChanged && (
            <Button 
              variant="contained" 
              color="success" 
              onClick={saveOrder}
              sx={{ ml: 2 }}
            >
              Save Order
            </Button>
          )}
        </div>
      )}

      <DndContext 
        sensors={sensors} 
        collisionDetection={closestCenter} 
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={displayOrder.map(member => member.hierarchyOrder.toString())} 
          strategy={verticalListSortingStrategy}
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>S No</StyledTableCell>
                  <StyledTableCell>Member Name</StyledTableCell>
                  <StyledTableCell>Position</StyledTableCell>
                  {isAdmin && <StyledTableCell>Actions</StyledTableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayOrder.map((member, index) => (
                  <SortableRow
                    key={member.hierarchyOrder}
                    member={member}
                    index={index}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    isAdmin={isAdmin}
                    isEditing={editId === member.hierarchyOrder}
                    editedMember={editedMember}
                    handleEditChange={handleEditChange}
                    handleSave={handleSave}
                    handleCancel={handleCancel}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default AdministrationMembers;